import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  MagnifyingGlass, Copy, DownloadSimple, Printer, Star, CheckCircle,
  Archive, PencilSimple, Trash, ClockCounterClockwise, FileText,
} from "@phosphor-icons/react";
import CustomerLink from "@/components/CustomerLink";
import { useAuth } from "@/context/AuthContext";

const FILTERS = [
  { key: "All", label: "All" },
  { key: "Draft", label: "Draft" },
  { key: "Approved", label: "Approved" },
  { key: "Archived", label: "Archived" },
  { key: "Favourite", label: "★ Favourite" },
  { key: "Today", label: "Today" },
];

export default function DraftLibrary({ customerId }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [view, setView] = useState(null);       // full draft object
  const [editing, setEditing] = useState(null); // { id, content }
  const [confirmDel, setConfirmDel] = useState(null);
  const [versionOpen, setVersionOpen] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (customerId) params.customer_id = customerId;
    if (filter === "Draft" || filter === "Approved" || filter === "Archived") params.status = filter;
    if (filter === "Favourite") params.favourite = true;
    if (filter === "Today") params.days = 1;
    if (q.trim()) params.q = q.trim();
    const [r, s] = await Promise.all([
      api.get("/ai/drafts", { params }),
      api.get("/ai/drafts/stats"),
    ]);
    setRows(r.data); setStats(s.data);
  }, [customerId, filter, q]);
  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => rows, [rows]);

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/ai/drafts/${id}`, body);
      setRows((prev) => prev.map((r) => r.id === id ? data : r));
      if (view?.id === id) setView(data);
      if (editing?.id === id) setEditing({ id, content: data.content });
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const toggleFavourite = (r) => patch(r.id, { favourite: !r.favourite }).then(() => toast.success(r.favourite ? "Removed from favourites" : "Added to favourites"));
  const setStatus = (r, status) => patch(r.id, { status }).then(() => toast.success(`Status → ${status}`));

  const saveEdit = async () => {
    if (!editing) return;
    const data = await patch(editing.id, { content: editing.content });
    if (data) {
      toast.success(`Saved · v${(data.versions || []).length + 1}`);
      setEditing(null);
    }
  };

  const restoreVersion = async (draftId, versionNo) => {
    try {
      const { data } = await api.post(`/ai/drafts/${draftId}/restore/${versionNo}`);
      setRows((prev) => prev.map((r) => r.id === draftId ? data : r));
      setView(data);
      toast.success(`Restored to v${versionNo}`);
      setVersionOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const del = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/ai/drafts/${confirmDel.id}`);
      setRows((prev) => prev.filter((r) => r.id !== confirmDel.id));
      setConfirmDel(null);
      toast.success("Draft deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const copy = (r) => { navigator.clipboard.writeText(r.content || ""); toast.success("Copied"); };

  const downloadTxt = (r) => {
    const blob = new Blob([r.content || ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(r.template_name || "draft").replace(/\s/g, "_")}_${r.id.slice(0, 6)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDocx = async (r) => {
    try {
      const res = await api.get(`/ai/drafts/${r.id}/docx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(r.template_name || "draft").replace(/\s/g, "_")}_${r.id.slice(0, 6)}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Download failed"); }
  };

  const doPrint = (r) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked"); return; }
    const pre = w.document.createElement("pre");
    pre.style.fontFamily = "'IBM Plex Sans',sans-serif";
    pre.style.fontSize = "13px";
    pre.style.whiteSpace = "pre-wrap";
    pre.style.padding = "32px";
    pre.style.maxWidth = "720px";
    pre.style.margin = "auto";
    pre.textContent = r.content || "";
    w.document.body.appendChild(pre);
    w.document.title = r.template_name || "Draft";
    w.print();
  };

  const canDelete = user && ["admin", "super_admin", "branch_admin"].includes(user.role);

  return (
    <div className="space-y-4" data-testid="draft-library">
      {!customerId && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatBox label="Total" value={stats.total || 0} tone="blue" />
          <StatBox label="Today" value={stats.today || 0} tone="orange" />
          <StatBox label="Draft" value={stats.draft || 0} tone="amber" />
          <StatBox label="Approved" value={stats.approved || 0} tone="green" />
          <StatBox label="Archived" value={stats.archived || 0} tone="slate" />
        </div>
      )}

      <Card className="p-3 bg-white border-slate-200">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 relative min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="draft-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search content, template, customer, language…" className="pl-9 h-9" />
          </div>
          <div className="flex gap-1 flex-wrap">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`draft-filter-${f.key.toLowerCase()}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.key ? "bg-orange-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 overflow-hidden" data-testid="draft-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Template</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Customer</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Language</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Created</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-16">No drafts found. Generate one from the Generate tab.</TableCell></TableRow>
            )}
            {filteredRows.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50">
                <TableCell>
                  <button onClick={() => setView(r)} data-testid={`draft-open-${r.id}`} className="text-left">
                    <div className="flex items-center gap-1.5">
                      {r.favourite && <Star size={12} weight="fill" className="text-amber-500" />}
                      <span className="font-medium text-sm text-slate-900 hover:text-orange-700">{r.template_name}</span>
                    </div>
                    {r.template_group && <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{r.template_group}</div>}
                  </button>
                </TableCell>
                <TableCell>{r.customer_id ? <CustomerLink id={r.customer_id} name={r.customer_name || "—"} /> : <span className="text-xs text-slate-500">{r.customer_name || "—"}</span>}</TableCell>
                <TableCell className="text-xs">{r.language}</TableCell>
                <TableCell className="text-xs font-mono text-slate-600">{(r.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${
                    r.status === "Approved" ? "border-emerald-300 text-emerald-700 bg-emerald-50" :
                    r.status === "Archived" ? "border-slate-300 text-slate-600 bg-slate-50" :
                    "border-amber-300 text-amber-700 bg-amber-50"
                  }`}>{r.status || "Draft"}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <ActionBtn onClick={() => toggleFavourite(r)} title={r.favourite ? "Un-favourite" : "Favourite"} testid={`draft-fav-${r.id}`}>
                      <Star size={13} weight={r.favourite ? "fill" : "regular"} className={r.favourite ? "text-amber-500" : ""} />
                    </ActionBtn>
                    <ActionBtn onClick={() => setEditing({ id: r.id, content: r.content })} title="Edit" testid={`draft-edit-${r.id}`}>
                      <PencilSimple size={13} />
                    </ActionBtn>
                    <ActionBtn onClick={() => copy(r)} title="Copy" testid={`draft-copy-${r.id}`}>
                      <Copy size={13} />
                    </ActionBtn>
                    <ActionBtn onClick={() => downloadDocx(r)} title="Download DOCX" testid={`draft-docx-${r.id}`}>
                      <FileText size={13} />
                    </ActionBtn>
                    <ActionBtn onClick={() => downloadTxt(r)} title="Download TXT" testid={`draft-txt-${r.id}`}>
                      <DownloadSimple size={13} />
                    </ActionBtn>
                    <ActionBtn onClick={() => doPrint(r)} title="Print" testid={`draft-print-${r.id}`}>
                      <Printer size={13} />
                    </ActionBtn>
                    {r.status !== "Approved" && <ActionBtn onClick={() => setStatus(r, "Approved")} title="Approve" tone="green" testid={`draft-approve-${r.id}`}><CheckCircle size={13} /></ActionBtn>}
                    {r.status !== "Archived" && <ActionBtn onClick={() => setStatus(r, "Archived")} title="Archive" testid={`draft-archive-${r.id}`}><Archive size={13} /></ActionBtn>}
                    {r.status === "Archived" && <ActionBtn onClick={() => setStatus(r, "Draft")} title="Restore" testid={`draft-restore-${r.id}`}><ClockCounterClockwise size={13} /></ActionBtn>}
                    {canDelete && <ActionBtn onClick={() => setConfirmDel(r)} title="Delete" tone="red" testid={`draft-del-${r.id}`}><Trash size={13} /></ActionBtn>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* View / preview dialog */}
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden bg-white flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }} className="flex items-center gap-2 flex-wrap">
              <span>{view?.template_name}</span>
              {view?.favourite && <Star size={14} weight="fill" className="text-amber-500" />}
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{view?.status || "Draft"}</Badge>
              {view?.versions?.length > 0 && <Badge variant="outline" className="text-[10px]" data-testid="draft-view-versions">{view.versions.length + 1} versions</Badge>}
            </DialogTitle>
            <DialogDescription className="sr-only">View the full draft content, versions, and metadata.</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="overflow-y-auto flex-1 space-y-2">
              <div className="text-xs text-slate-500">
                {view.customer_name || "No customer"} · {view.language} · created {(view.created_at || "").slice(0, 16).replace("T", " ")} by {view.created_by || "—"}
              </div>
              <pre className="bg-slate-50 border border-slate-200 rounded-md p-4 whitespace-pre-wrap text-sm font-mono max-h-[60vh] overflow-y-auto">{view.content}</pre>
            </div>
          )}
          <DialogFooter className="pt-3 flex-wrap gap-2">
            {view?.versions?.length > 0 && <Button variant="outline" onClick={() => setVersionOpen(true)} data-testid="draft-view-history"><ClockCounterClockwise size={14} className="mr-1" /> Version History</Button>}
            {view && <Button variant="outline" onClick={() => setEditing({ id: view.id, content: view.content })}><PencilSimple size={14} className="mr-1" /> Edit</Button>}
            {view && <Button variant="outline" onClick={() => downloadDocx(view)}><FileText size={14} className="mr-1" /> DOCX</Button>}
            {view && <Button variant="outline" onClick={() => doPrint(view)}><Printer size={14} className="mr-1" /> Print</Button>}
            <Button onClick={() => setView(null)} className="bg-slate-900 hover:bg-slate-800 text-white">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden bg-white flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Edit Draft</DialogTitle>
            <DialogDescription className="sr-only">Modify the draft. Saving creates a new version so you can restore the previous one later.</DialogDescription>
          </DialogHeader>
          {editing && (
            <Textarea
              data-testid="draft-edit-textarea"
              value={editing.content}
              onChange={(e) => setEditing({ ...editing, content: e.target.value })}
              className="flex-1 min-h-[60vh] font-mono text-sm"
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="draft-edit-save" onClick={saveEdit} className="bg-orange-600 hover:bg-orange-700 text-white">Save · new version</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version history */}
      <Dialog open={versionOpen} onOpenChange={setVersionOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Version History · {view?.template_name}</DialogTitle>
            <DialogDescription className="sr-only">Previous versions of this draft. Restore any version to become the current content.</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="space-y-2 py-2">
              <div className="p-3 rounded-md border border-emerald-300 bg-emerald-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-emerald-800">Current (v{(view.versions?.length || 0) + 1})</div>
                  <div className="text-[10px] text-emerald-700">{(view.updated_at || "").slice(0, 16).replace("T", " ")} · {view.updated_by}</div>
                </div>
              </div>
              {(view.versions || []).slice().reverse().map((v) => (
                <div key={v.version_no} className="p-3 rounded-md border border-slate-200" data-testid={`draft-version-${v.version_no}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-slate-900">v{v.version_no}</div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-500">{(v.edited_at || "").slice(0, 16).replace("T", " ")} · {v.edited_by}</span>
                      <Button size="sm" variant="outline" onClick={() => restoreVersion(view.id, v.version_no)} data-testid={`draft-restore-v${v.version_no}`}>Restore</Button>
                    </div>
                  </div>
                  <pre className="mt-2 text-[11px] font-mono text-slate-600 max-h-32 overflow-y-auto bg-slate-50 p-2 rounded whitespace-pre-wrap">{(v.content || "").slice(0, 500)}{(v.content || "").length > 500 ? "…" : ""}</pre>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "var(--font-heading)" }}>Delete draft?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmDel?.template_name}</b> for {confirmDel?.customer_name || "no customer"} will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="draft-del-confirm" className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatBox({ label, value, tone }) {
  const t = { blue: "text-blue-700", orange: "text-orange-600", amber: "text-amber-700", green: "text-emerald-700", slate: "text-slate-700" }[tone] || "text-slate-900";
  return (
    <div className="p-3 rounded-md border border-slate-200 bg-white">
      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 ${t}`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function ActionBtn({ children, onClick, title, tone, testid }) {
  const t = tone === "red" ? "text-red-600 hover:bg-red-50" : tone === "green" ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-600 hover:bg-slate-100";
  return (
    <button data-testid={testid} onClick={onClick} title={title} className={`h-7 w-7 inline-flex items-center justify-center rounded-md transition-colors ${t}`}>
      {children}
    </button>
  );
}
